'use client';

import { useEffect } from 'react';
import { sportsEventsClient } from '@wl/api-client';

/**
 * Publishes the city chosen in Attractions to the two cross-module localStorage
 * entries, so Hotels / Shows / Sports / Concerts / Restaurants pick it up.
 *
 * Direct port of `app/sports/_components/CityRecentSearchSync.tsx` (and the
 * matching concerts one): same two keys, same strict blob shape, same
 * `searches[]` seeding, same dedup-against-the-first-entry and 3-entry cap,
 * same silent failure. Attractions was the only module reading the shared city
 * without ever writing it, which is why a city picked here never followed the
 * visitor anywhere else.
 *
 * WHY IT RUNS HERE, NOT ON SUBMIT: the sports version's own comment records
 * that seeding during form submit raced `router.push` and the fetch was
 * cancelled mid-flight, so the writes never landed. It runs on the destination
 * page instead, once the navigation has settled and the tree is stable. Same
 * reasoning applies here, so this mounts on the listing with the city the page
 * actually resolved.
 *
 * WHY ONLY AN EXPLICITLY CHOSEN CITY: mounted only when the URL named a city —
 * i.e. the visitor searched or picked one here. A geo default, or a city this
 * module autofilled from someone else's search, must NOT be published: writing
 * the geo default would quietly overwrite the visitor's real choice for every
 * other module. This mirrors sports, where the writer lives on
 * `/sports/city/[slug]` (an explicitly chosen city page) and never on the
 * sports home.
 *
 * The CMS `SearchLocation` lookup is `getCityDestId`, the same endpoint sports,
 * concerts and restaurants use. It is reached through the shared
 * `@wl/api-client` singleton rather than by importing another domain's config,
 * so route isolation holds.
 */

/**
 * Today + tomorrow as `YYYY-MM-DD`. Mirrors the hotels SearchBar's date format
 * so the `searches[]` entry is indistinguishable from one hotels wrote itself.
 */
function getCurrentAndNextDay(): { startDate: string; endDate: string } {
  const today = new Date();
  const fmt = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const end = new Date(start);
  end.setDate(start.getDate() + 1);
  return { startDate: fmt(start), endDate: fmt(end) };
}

interface CityRecentSearchSyncProps {
  /** The city the listing resolved. Empty → nothing to publish. */
  cityName: string;
}

export function CityRecentSearchSync({ cityName }: CityRecentSearchSyncProps) {
  useEffect(() => {
    if (!cityName) return;
    let cancelled = false;
    const cityNameQuery = cityName.split(',')[0].trim();
    if (!cityNameQuery) return;

    sportsEventsClient
      .getCityDestId(cityNameQuery)
      .then((res) => {
        if (cancelled) return;
        if (res?.message !== 'success' || !res?.data) return;

        const doc = res.data as {
          city?: string;
          country?: string;
          destination_id?: string;
          state?: string;
          _id?: string;
        };

        /*
         * STRICT shape (spec 2026-08-05), as enforced by concerts
         * `ConcertsHomeListing` and `RestaurantsHero`: every required field must
         * be present, or we write NOTHING. A row missing `_id` or
         * `destination_id` is a CMS data gap, and papering over it with an empty
         * string leaks a broken value into every other module — hotels keys its
         * own recent-search dedup off `dest_id`.
         *
         * (The sports copy uses `|| ''` fallbacks here and can emit a partial
         * blob. That is the outlier, not the spec, so this follows concerts and
         * restaurants.)
         */
        if (!doc.city || !doc._id || !doc.destination_id) return;

        try {
          localStorage.setItem(
            'theatre-searches-city',
            JSON.stringify({
              city: doc.city,
              country: doc.country || '',
              // `id` is the CountryCity `_id`, which the SearchLocation row
              // exposes as `destination_id`; `dest_id` is the hotels-side id.
              id: doc.destination_id,
              state: doc.state || '',
              dest_id: doc._id,
            }),
          );

          // Hotels `searches[]` — same shape the hotels SearchBar writes, capped
          // at 3, deduped against the first entry.
          const { startDate, endDate } = getCurrentAndNextDay();
          const newSearch = {
            dest_id: String(doc._id),
            checkin: startDate,
            checkout: endDate,
            rooms: 1,
            adults: '2',
            children: '',
            ratecode: '',
            // Without `_label` the hotels home renders the card as a generic
            // "Recent search" — it rejects a raw ObjectId as a label.
            _label: [doc.city, doc.state, doc.country].filter(Boolean).join(', '),
          };
          const previousSearches = JSON.parse(localStorage.getItem('searches') || '[]');
          const sameAsFirst =
            Array.isArray(previousSearches) &&
            previousSearches.length > 0 &&
            JSON.stringify(previousSearches[0]) === JSON.stringify(newSearch);
          if (sameAsFirst) return;
          const updated = Array.isArray(previousSearches) ? previousSearches.slice() : [];
          updated.unshift(newSearch);
          if (updated.length > 3) updated.pop();
          localStorage.setItem('searches', JSON.stringify(updated));
        } catch {
          // localStorage disabled / quota / parse error — leave existing
          // entries untouched.
        }
      })
      .catch(() => {
        // Backend unreachable — skip the writes silently. The listing renders
        // regardless; cross-module seeding is a convenience, not a dependency.
      });

    return () => {
      cancelled = true;
    };
  }, [cityName]);

  return null;
}
